import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import type {
  AdditionalFieldsFormData,
  MapPlotRequestDetailAdditionalFields,
} from "@/features/map/types";

/**
 * バックエンドから返される追加項目のデータ型（isDeletedフィールドを含む）
 */
export interface AdditionalFieldItemWithMeta {
  value: string;
  isDeleted?: boolean;
}

export type AdditionalFieldsRecord = Record<
  string,
  AdditionalFieldItemWithMeta
>;

/**
 * additionalFields が有効な値を持っているかチェックする
 * null, 空オブジェクトの場合は false を返す
 */
export const hasValidAdditionalFields = (
  additionalFields: AdditionalFieldsRecord | null | undefined
): boolean => {
  if (!additionalFields) {
    return false;
  }

  // 空オブジェクトの場合は無効
  if (Object.keys(additionalFields).length === 0) {
    return false;
  }

  return true;
};

/**
 * additionalFields を AdditionalFieldsFormData に変換する
 * バックエンドから返されたデータ（valueとメタデータを含む）からvalueのみを抽出
 * isDeleted: true の項目は除外する
 */
export const parseAdditionalFields = (
  additionalFields: AdditionalFieldsRecord | null | undefined
): AdditionalFieldsFormData => {
  if (!additionalFields) {
    return {};
  }

  // value のみを抽出、isDeletedがtrueの項目は除外
  const result: AdditionalFieldsFormData = {};
  for (const [key, item] of Object.entries(additionalFields)) {
    // isDeletedがtrueの場合はスキップ
    if (item.isDeleted === true) {
      continue;
    }
    // valueフィールドを使用（真偽値の場合も文字列に変換）
    result[key] =
      item.value !== null && item.value !== undefined ? String(item.value) : "";
  }
  return result;
};

/**
 * AdditionalFieldsFormData を MapPlotRequestDetailAdditionalFields に変換する
 * 値が入力されているフィールドのみを含める（空文字列は除外）
 */
export const convertFormDataToAdditionalFields = (
  data: AdditionalFieldsFormData,
  availableFields: AdditionalFieldConfig
): MapPlotRequestDetailAdditionalFields => {
  const result: MapPlotRequestDetailAdditionalFields = {};

  // availableFields.fieldsに定義されているフィールドのうち、値が入力されているもののみを処理
  if (!availableFields?.fields) {
    return result;
  }

  for (const field of availableFields.fields) {
    const value = data[field.id] ?? "";

    // 空文字列のフィールドは除外
    if (value === "") {
      continue;
    }

    result[field.id] = {
      value,
    };
  }

  return result;
};

/**
 * string (JSON) 形式の additionalFields を AdditionalFieldsFormData に変換する
 * AdditionalFieldsModalに渡す前の変換に使用
 * {uuid: {value: "..."}} 形式から {uuid: "..."} 形式に変換
 */
export const convertStringToAdditionalFields = (
  additionalFields:
    | string
    | Record<string, { value: string }>
    | null
    | undefined,
  availableFields: AdditionalFieldConfig | undefined
): AdditionalFieldsFormData => {
  if (!additionalFields || !availableFields) {
    return {};
  }

  try {
    // 既にオブジェクトの場合はそのまま使用、文字列の場合はパース
    const parsed =
      typeof additionalFields === "string"
        ? (JSON.parse(additionalFields) as Record<string, { value: string }>)
        : additionalFields;

    const result: AdditionalFieldsFormData = {};

    // {uuid: {value: "..."}} 形式から {uuid: "..."} 形式に変換
    Object.entries(parsed).forEach(([key, val]) => {
      if (typeof val === "object" && val !== null && "value" in val) {
        // NOTE: React Hook Formでは全ての値を文字列として扱う必要があるため、
        // number型のフィールドでもString()で明示的に文字列に変換する
        result[key] =
          val.value !== null && val.value !== undefined
            ? String(val.value)
            : "";
      }
    });

    return result;
  } catch {
    return {};
  }
};
